import { trpc } from "@/Utils/trpc";
import { LogoTile } from "@/components/LogoTile";

import { useCallback } from "react";

type Props = {
  displayName: string;
  terminalId: string;
  size?: number;
};

export const BuildingLogoView: React.FC<Props> = ({
  displayName,
  terminalId,
  size = 33,
}) => {
  const trpcUtils = trpc.useUtils();

  const {
    data: logoData,
    refetch,
    isLoading,
  } = trpc.buildingStorage.getLogo.useQuery({ terminalId });

  const { mutate: deleteLogoFromStorage } =
    trpc.buildingStorage.deleteLogo.useMutation();

  const getUploadUrl = useCallback(
    async (filename: string) => {
      return trpcUtils.buildingStorage.getUploadUrl.fetch({
        filename,
        terminalId,
      });
    },
    [trpcUtils, terminalId],
  );

  const { mutateAsync: cleanUpLogoFilesMutation } =
    trpc.buildingStorage.cleanUpLogoFiles.useMutation();

  const cleanUpLogoFiles = useCallback(
    async (oldFileName: string) => {
      await cleanUpLogoFilesMutation({ oldFileName, terminalId });
    },
    [cleanUpLogoFilesMutation, terminalId],
  );

  const deleteLogo = useCallback(
    (fileName: string, options: { onSuccess: () => void }) => {
      deleteLogoFromStorage(
        { fileName, terminalId },
        { onSuccess: options.onSuccess },
      );
    },
    [deleteLogoFromStorage, terminalId],
  );

  if (isLoading) {
    return <div className="h-[52.8px] w-[52.8px] rounded-md bg-neutral-100" />;
  }

  return (
    <div className="relative">
      <LogoTile
        logoUrl={logoData?.url ?? null}
        displayName={displayName || "?"}
        size={size}
        variant="rounded"
        enableFileMenu
        buttonPosition="left"
        uploadConfig={{
          getUploadUrl,
          cleanUpLogoFiles,
          deleteLogo,
          onComplete: refetch,
          currentFileName: logoData?.name,
        }}
      />
    </div>
  );
};
